import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { AUDIENCE_TARGETING_KINDS, type Campaign, type Targeting } from '@feathr/blackbox';
import { FormSummaryItem, Pill, Skeleton } from '@feathr/components';
import { useStore } from '@feathr/extender/state';

import { CampaignContext } from '../CampaignEditPage/CampaignEditPage.context';

import * as styles from './AudienceTargetsSummary.css';

interface IProps {
  campaign: Campaign;
  validationErrors?: {
    targets?: string[];
  };
}

const AudienceTargetsSummary = observer(
  ({ validationErrors, campaign }: IProps): JSX.Element | null => {
    const { t } = useTranslation();
    const { Segments, Targetables } = useStore();
    const { targetings } = useContext(CampaignContext);
    const { isOmniReachCampaign } = campaign;

    function filterTargetings(trgt: Targeting): boolean {
      return (
        AUDIENCE_TARGETING_KINDS.includes(trgt.get('kind')) &&
        !trgt.get('is_archived') &&
        !!trgt.get('target_data')
      );
    }

    function getDisplayName(trgt: Targeting): string {
      const isSegment = trgt.get('kind') === 'segment';
      if (isSegment) {
        return Segments.get(trgt.get('target_data', ''))?.get('name') ?? '';
      }
      return Targetables.get(trgt.get('target_data', ''))?.get('name') ?? '';
    }

    function getSegmentOrTargetInclusionStatus(trgt: Targeting): JSX.Element | null {
      const isIncluded = trgt.get('included');

      return (
        <Pill theme={isIncluded ? 'slate' : 'red'} variant={'primary'}>
          {isIncluded ? t('Included') : t('Excluded')}
        </Pill>
      );
    }

    const filteredTargetings: Targeting[] =
      targetings?.models.filter(filterTargetings).sort((a, b) => {
        return getDisplayName(a).localeCompare(getDisplayName(b));
      }) ?? [];

    const hasTargetings = filteredTargetings.length > 0;

    // Pull what's in FiltersSummary instead
    // Surface groups in the review step to surface if they weren't reset as expected
    if (isOmniReachCampaign && !hasTargetings) {
      return null;
    }

    const targetingListItems = filteredTargetings.map((trgt) => {
      const displayName = getDisplayName(trgt);
      return (
        <li className={styles.targetItem} key={trgt.id}>
          <div className={styles.targetContent}>
            <span>{displayName}</span>
            {getSegmentOrTargetInclusionStatus(trgt)}
          </div>
        </li>
      );
    });

    if (targetings?.isPending) {
      return (
        <FormSummaryItem
          label={t('Audience')}
          name={'summary_targets'}
          value={<Skeleton paragraphs={3} width={250} />}
        />
      );
    }

    return (
      <FormSummaryItem
        errors={validationErrors?.targets}
        label={t('Audience')}
        name={'summary_targets'}
        value={hasTargetings ? <ol>{targetingListItems}</ol> : t('(None)')}
      />
    );
  },
);

AudienceTargetsSummary.displayName = 'AudienceTargetsSummary';

export default AudienceTargetsSummary;
